import React from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FIELDS = [
  { key: "k1", label: "K1 (D)", step: "0.01" },
  { key: "k1_axis", label: "K1 axis (°)", step: "1" },
  { key: "k2", label: "K2 (D)", step: "0.01" },
  { key: "k2_axis", label: "K2 axis (°)", step: "1" },
  { key: "al", label: "AL (mm)", step: "0.01" },
  { key: "acd", label: "ACD (mm)", step: "0.01" },
  { key: "lt", label: "LT (mm)", step: "0.01" },
  { key: "wtw", label: "WTW (mm)", step: "0.01" },
];

function EyePanel({ eye, setEye, side, color }) {
  const upd = (k) => (e) =>
    setEye((p) => ({ ...p, [k]: e.target.value }));

  const avgK =
    parseFloat(eye.k1) && parseFloat(eye.k2)
      ? ((parseFloat(eye.k1) + parseFloat(eye.k2)) / 2).toFixed(2)
      : "—";
  const cyl =
    parseFloat(eye.k1) && parseFloat(eye.k2)
      ? (parseFloat(eye.k2) - parseFloat(eye.k1)).toFixed(2)
      : "—";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ background: color }}
          />
          <div className="font-serif text-lg font-semibold">
            {side === "OD" ? "OD · Right Eye" : "OS · Left Eye"}
          </div>
        </div>
        <div className="text-xs text-slate-500 space-x-3">
          <span>Avg K <span className="font-mono text-slate-800">{avgK}</span></span>
          <span>Cyl <span className="font-mono text-slate-800">{cyl}</span></span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              {f.label}
            </Label>
            <Input
              type="number"
              step={f.step}
              value={eye[f.key]}
              onChange={upd(f.key)}
              data-testid={`${side.toLowerCase()}-${f.key}-input`}
              className="mt-1 font-mono"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BiometryGrid({ od, os, setOd, setOs }) {
  return (
    <Card className="p-5 sm:p-6 border-slate-200">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold tracking-tight">Biometry Data</h2>
          <p className="text-sm text-slate-500 mt-1">
            Verify or edit values extracted from the scan. K1 must be the flatter meridian.
          </p>
        </div>
        <div className="hidden sm:block text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          Step 03
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EyePanel eye={od} setEye={setOd} side="OD" color="hsl(214 95% 46%)" />
        <EyePanel eye={os} setEye={setOs} side="OS" color="hsl(158 64% 40%)" />
      </div>
    </Card>
  );
}
